import './MyProfile.css';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { EditOutlined, PictureOutlined } from '@ant-design/icons';
import { Input } from 'antd';
import Button from '@views/Button';
import { udpateUserStats } from '@service/user';

import Metas from '@views/Metas';
import { UserPropsTypes } from '@models/User';

export const saveMyProfile = (changes) => (dispatch, getState) => {
  const { jwToken, user } = getState();
  dispatch({ type: 'USER_SAVING', payload: true });
  return udpateUserStats(jwToken, user._id, changes)
    .then(() => {
      dispatch({ type: 'AUTH_UPDATE_USER', payload: changes });
      return { ok: true };
    })
    .catch((err) => ({
      ok: false,
      error:
        err?.response?.data?.message ||
        'No se pudo guardar el perfil. Intentá de nuevo.',
    }))
    .finally(() => {
      dispatch({ type: 'USER_SAVING', payload: false });
    });
};

export const MyProfile = ({ user, saving, onSaveProfile }) => {
  const imageInputRef = React.useRef(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftDni, setDraftDni] = React.useState(user?.dni || '');
  const [draftProfilePicture, setDraftProfilePicture] = React.useState(
    user?.profilePicture || ''
  );
  const [profileImageError, setProfileImageError] = React.useState('');
  const [saveError, setSaveError] = React.useState('');
  const [showImageFallback, setShowImageFallback] = React.useState(false);
  const hasValidProfilePicture =
    !!(isEditing ? draftProfilePicture : user?.profilePicture) &&
    !showImageFallback;

  const openImagePicker = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  const onImageSelected = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isSupported = ['image/png', 'image/jpeg'].includes(file.type);
    if (!isSupported) {
      setProfileImageError('Formato inválido. Usá PNG o JPG.');
      return;
    }

    const maxSizeMB = 5;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setProfileImageError('La imagen supera los 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageBase64 = String(reader.result || '');
      setProfileImageError('');
      setShowImageFallback(false);
      setDraftProfilePicture(imageBase64);
    };
    reader.onerror = () => {
      setProfileImageError('No se pudo leer la imagen seleccionada.');
    };
    reader.readAsDataURL(file);
  };

  const startEditing = () => {
    setDraftDni(user?.dni || '');
    setDraftProfilePicture(user?.profilePicture || '');
    setProfileImageError('');
    setSaveError('');
    setShowImageFallback(false);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setProfileImageError('');
    setSaveError('');
    setShowImageFallback(false);
  };

  const canSave =
    !!draftDni &&
    draftDni.length >= 6 &&
    /^[0-9]+$/.test(draftDni) &&
    !profileImageError;

  const handleSave = async () => {
    setSaveError('');
    const response = await onSaveProfile({
      dni: draftDni.trim(),
      profilePicture: draftProfilePicture,
    });
    if (response?.ok) {
      setIsEditing(false);
      return;
    }
    setSaveError(response?.error || 'No se pudo guardar el perfil.');
  };

  return (
    <div className="MyProfile">
      <Metas title="Mi perfil en Prode 2026" path="myprofile" mobileNarrow />
      <section className="my-profile-hero">
        <h1>Mi perfil</h1>
        <p>
          Completá tus datos. Es importante que los datos sean verdaderos para
          poder contactarte en caso de ganar.
        </p>
      </section>

      <section className="my-profile-card">
        <button
          type="button"
          className="my-profile-edit-btn"
          onClick={isEditing ? cancelEditing : startEditing}
          aria-label={isEditing ? 'Cancelar edición' : 'Editar perfil'}
        >
          <EditOutlined />
        </button>

        <div className="my-profile-avatar-block">
          <div className="my-profile-avatar">
            {hasValidProfilePicture ? (
              <img
                src={isEditing ? draftProfilePicture : user.profilePicture}
                alt="Foto de perfil"
                onError={() => setShowImageFallback(true)}
              />
            ) : (
              <PictureOutlined />
            )}
          </div>
          {isEditing ? (
            <button
              type="button"
              className="my-profile-photo-edit-circle"
              onClick={openImagePicker}
              aria-label="Cambiar foto de perfil"
            >
              <EditOutlined />
            </button>
          ) : null}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="profile-image-input"
            onChange={onImageSelected}
          />
          {profileImageError ? (
            <p className="danger my-profile-error">{profileImageError}</p>
          ) : null}
        </div>

        <div className="my-profile-content">
          <h2>Datos personales</h2>
          <p className="my-profile-username">
            {user?.username || '-'}
          </p>

          <div className="my-profile-data-grid">
            <div>
              <h3>DNI</h3>
              {isEditing ? (
                <Input
                  value={draftDni}
                  maxLength={12}
                  onChange={(e) =>
                    setDraftDni(e.target.value.replace(/[^0-9]/g, ''))
                  }
                />
              ) : (
                <p>{user?.dni || '-'}</p>
              )}
            </div>
          </div>

          <div className="my-profile-stats-grid">
            <div>
              <h4>Puntos</h4>
              <strong>{user?.score ?? 0}</strong>
            </div>
            <div>
              <h4>Puesto</h4>
              <strong>{user?.rank ?? '-'}</strong>
            </div>
          </div>

          {isEditing ? (
            <div className="my-profile-edit-actions">
              <Button onClick={cancelEditing} type="secondary">
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave}
                loading={saving}
                className="my-profile-save-btn"
              >
                Guardar cambios
              </Button>
            </div>
          ) : null}
          {saveError ? <p className="danger my-profile-error">{saveError}</p> : null}
        </div>
      </section>
    </div>
  );
};

MyProfile.propTypes = {
  user: PropTypes.shape(UserPropsTypes),
  saving: PropTypes.bool,
  onSaveProfile: PropTypes.func,
};

MyProfile.defaultProps = {
  user: {},
  saving: false,
  onSaveProfile: () => Promise.resolve({ ok: false }),
};

export const mapStateToProps = (state) => ({
  user: state.user,
  saving: state.savingUser,
});

export const mapDispatchToProps = (dispatch) => ({
  onSaveProfile: (changes) => dispatch(saveMyProfile(changes)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile);
